import React, { MutableRefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './LinkBlock.module.scss';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { CSSTransition } from 'react-transition-group';
import { useDidMount } from '../../../hooks/useDidMount';
import { LinkEditModal } from '../LinkEditModal';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { GetLinkMetadataQuery, PostStatus, useGetLinkMetadataLazyQuery } from '../../../models/graphql';
import { showToast } from '../../../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../../../constants/apollo-errors';
import { ApolloError } from '@apollo/client';
import { PostEditorLink } from '../../../models/post-editor';
import { idGenerator } from '../../../helpers/idGenerator';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';
import { EditorRefType, PostEditorContext } from '../states/PostEditorProvider';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ErrorMessageSeverity } from 'constants/form';
import { getErrorMessages } from '../validators/helpers/getErrorMessages';
import { getCustomizationProfileId } from '../validators/helpers/customization-getter';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { formatErrorMessages } from '../validators/helpers/formatErrorMessages';
import { PostEditorErrorKeys } from 'models/validator-props';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { POST_EDITOR_ERROR_CLASS } from '../../../constants/styleConstants';

export type LinkBlockViewProps = {
  //
  link: PostEditorLink;
  onRemove: () => void;
  hasLinkInText: boolean;
  profiles: SocialProfileWithBoardsAndMentionType[] | null;
  updateLinkMetadata: (linkMetadata: GetLinkMetadataQuery['linkMetadata'], initialUrl: string) => void;
  editorRefs: MutableRefObject<EditorRefType[][]>;
};

const errorsList: PostEditorErrorKeys[] = ['checkLinksPresent', 'checkLinkPreviewSuppressed'];

export const LinkBlockView = (props: LinkBlockViewProps): JSX.Element => {
  const { link, onRemove, hasLinkInText, profiles, updateLinkMetadata, editorRefs } = props;
  const { initialUrl, linkMetadata } = link;
  const { initialUrl: initialLinkMetadataUrl } = linkMetadata || {};
  const [getLinkMetadata, getLinkMetadataResults] = useGetLinkMetadataLazyQuery();
  const { error: linkMetadataResultsError, loading: loadingLinkMetadata, data } = getLinkMetadataResults;
  const [linkMetadataError, setLinkMetadataError] = useState<ApolloError | undefined>(linkMetadataResultsError);
  const [mount, setMount] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);

  const { state } = useContext(PostEditorContext);
  const { postGroupIndex } = tabsState;

  const postGroup = state.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts, errors } = postGroup;
  const isCustomizingByProfile = customizeContentPerProfile && typeof customizeIndex === 'number';

  const customizedProfileId = getCustomizationProfileId(postGroup, customizeIndex);
  const isPostDraft = isCustomizingByProfile
    ? posts[customizeIndex].postStatus === PostStatus.DRAFT
    : postGroup.groupPostAttributes.postStatus === PostStatus.DRAFT;

  const hasError =
    !isPostDraft && hasSeverity(errors, ErrorMessageSeverity.error, customizedProfileId, undefined, errorsList);

  const errorMessages = getErrorMessages(errors, customizedProfileId, undefined, errorsList);

  const tooltipId = useMemo(() => idGenerator('tooltipTargetLinkBlock'), []);

  useDidMount(() => {
    // we need to setMount to true, since we wnat to trigger animation (fade) only when the component has been completely mounted.
    setMount(true);
    return () => {
      setMount(false);
    };
  });

  // if we are importing via bulk import, we fetch linkMetadata in bulk, so we want to skip it here
  useEffect(() => {
    if (initialUrl && initialLinkMetadataUrl !== initialUrl) {
      getLinkMetadata({
        variables: {
          urls: initialUrl
        }
      }).catch(e => {
        console.error(e);
        showToast({
          summary: 'Error fetching link metadata',
          severity: 'error',
          detail: GENERIC_ERROR_MESSAGE
        });
      });
    }
  }, [initialUrl, getLinkMetadata, initialLinkMetadataUrl]);

  // we use this use effect to prevent firing the same toaster error multiple times due to dependency update
  useEffect(() => {
    if (linkMetadataResultsError) {
      setLinkMetadataError(linkMetadataResultsError);
    }
  }, [linkMetadataResultsError]);

  useEffect(() => {
    if (data?.linkMetadata.length && initialUrl) {
      updateLinkMetadata(data.linkMetadata, initialUrl);
    }
  }, [data?.linkMetadata, updateLinkMetadata, initialUrl]);

  useEffect(() => {
    if (linkMetadataError) {
      triggerErrorToast('Error fetching link metadata')(linkMetadataError);
      // if we are editing from the modal we want to revert to the original link. if we are not editing in the modal, it means we are doing the initial link detection, thus we should remove the bad link
      if (showEdit) {
        // linkDispatch({ type: 'updateLink', payload: { link: link.initialUrl } });
      } else {
        // onRemove(editorRefs);
      }
      // TODO we need to see what are we going to do if get a link metadata error? Should we remove / revert the link ?
      setLinkMetadataError(undefined);
    }
  }, [linkMetadataError, link.initialUrl, onRemove, showEdit, editorRefs]);
  const classesLinkBlock = classNames(classes.LinkBlock, {
    [classes.LinkBlockError]: hasError,
    [POST_EDITOR_ERROR_CLASS]: hasError
  });
  return (
    <CSSTransition in={mount} timeout={300} classNames={'link-block'} nodeRef={cssRef} unmountOnExit>
      <div className={classesLinkBlock} ref={cssRef}>
        <div className={'grid-x align-middle'}>
          <div className={classNames(classes.LinkLabel, 'cell', 'shrink')}>
            {hasLinkInText ? 'Link' : 'Link Preview'}
          </div>
          <div className={'cell shrink'}>
            {!hasError && (
              <Tooltip
                target={`#${tooltipId}`}
                position={'right'}
                pointer
                type={'inlined'}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                This post contains a link that may be shown as a preview on supported social networks. Use this section
                to edit the link, including previews, UTM parameters, and tracking options.
              </Tooltip>
            )}
            {errorMessages.length > 0 && hasError && (
              <Tooltip
                icon={'alert'}
                target={`#tooltipTargetTextBlockError_${
                  customizeIndex !== undefined ? customizeIndex : 'group'
                }_${postGroupIndex}`}
                position={'right'}
                pointer={true}
                type={'inlined'}
                autoZIndex={false}
                style={{ zIndex: 2000 }}>
                {formatErrorMessages(errorMessages)}
              </Tooltip>
            )}
          </div>
        </div>
        <div className={'grid-x margin-top-small-16'}>
          <div
            className={
              'cell small-12 medium-auto tablet-portrait-12 large-auto margin-bottom-small-16 margin-bottom-medium-0 margin-bottom-tablet-portrait-16 margin-bottom-large-0'
            }>
            <div className={classes.ValueField}>
              <div className={classes.LinkTextWrapper}>{link.initialUrl}</div>
            </div>
          </div>
          <div className={'cell small-12 medium-shrink tablet-portrait-12 large-shrink'}>
            <div className={'grid-x'}>
              <div className={'cell auto medium-shrink tablet-portrait-auto large-shrink'}>
                <div className={classes.ButtonWrapperLeft}>
                  <Button
                    type={'button'}
                    variant={'solid'}
                    size={'large'}
                    color={ButtonColorsEnum.primary}
                    expanded
                    action={() => setShowEdit(true)}>
                    Edit
                  </Button>
                </div>
              </div>
              <div className={'cell auto medium-shrink tablet-portrait-auto large-shrink'}>
                <div className={classes.ButtonWrapperRight}>
                  <Button
                    type={'button'}
                    variant={'hollow'}
                    size={'large'}
                    color={ButtonColorsEnum.primary}
                    expanded
                    action={() => onRemove()}>
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showEdit && (
          <LinkEditModal link={link} setModal={setShowEdit} loading={loadingLinkMetadata} profiles={profiles} />
        )}
      </div>
    </CSSTransition>
  );
};
